// src/plugins/builder/dynamicRegistry.ts
// Exposes user-built (IndexedDB) plugins under the same RegistryEntry shape
// the static registry uses. The modified `src/plugins/registry.ts` in this
// delta calls `getDynamicEntries()` and merges its output with the Vite-glob
// static entries. That way, builder-created plugins show up in:
//
//   - listManifests() / listAvailable()
//   - loader.load()
//   - admin/pages/Plugins.tsx's "Installed" list
//
// No new runtime code has to import anything from the builder — the static
// side calls into this file once and gets the full merged picture.
import { getTemplate, currentUserPluginsSync } from './generate';
const listeners = new Set();
export function onDynamicChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
export function notifyDynamicChange() {
    for (const fn of listeners) {
        try {
            fn();
        }
        catch { /* ignore */ }
    }
}
// ----- adapt UserPluginRecord → RegistryEntry -------------------------------
function recordToEntry(rec) {
    const template = getTemplate(rec.templateId);
    if (!template) {
        // Template was removed in a future build. Skip rather than throw — the
        // admin page won't show it, and the record stays safe in IDB so a future
        // build that re-adds the template will pick it up.
        if (typeof console !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(`[builder] user plugin "${rec.instanceId}" uses template "${rec.templateId}" ` +
                `which isn't loaded. Skipping without data loss.`);
        }
        return null;
    }
    // Pull the manifest straight from the stored generated files. It was
    // validated against the template at install time.
    const manifest = { ...rec.generated.manifest };
    const ctx = {
        instanceId: rec.instanceId,
        instanceName: rec.name,
        config: rec.config,
    };
    const entry = { manifest };
    if (template.runtimeUi) {
        const RuntimeUi = template.runtimeUi;
        const Wrapped = function UserPluginView() {
            return RuntimeUi(ctx);
        };
        entry.loadComponent = async () => ({ default: Wrapped });
    }
    if (template.runtimeHooks) {
        const handlers = template.runtimeHooks;
        entry.loadHandlers = async () => ({ handlers, default: handlers });
    }
    return entry;
}
/** Build a map of all user plugins right now. Sync, safe to call in registry. */
export function getDynamicEntries() {
    const out = {};
    for (const rec of currentUserPluginsSync()) {
        const e = recordToEntry(rec);
        if (e)
            out[rec.instanceId] = e;
    }
    return out;
}
/** Just the manifests, for listManifests() / listAvailable() efficiency. */
export function getDynamicManifests() {
    return Object.values(getDynamicEntries()).map(e => e.manifest);
}
// Exported for tests that want to assert a user plugin round-trips to an entry.
export function __entryForRecordForTests(rec) {
    return recordToEntry(rec);
}
